import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { BulkActionBar } from '@/components/programming/BulkActionBar';
import { FilterBar, emptyFilterValues } from '@/components/programming/FilterBar';
import type { FilterValues } from '@/components/programming/FilterBar';
import { TVDisplayButton } from '@/components/programming/TVDisplayButton';
import { WorkoutCreatorMetadata } from '@/components/programming/WorkoutCreatorMetadata';
import { WorkoutDetail } from '@/components/programming/WorkoutDetail';
import {
  WorkoutForm,
  type WorkoutFormData,
  normalizeWorkoutFormData,
} from '@/components/programming/WorkoutForm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAdminApi } from '@/hooks/useAdminApi';
import { useSelection } from '@/hooks/useSelection';
import { useSubmissionLock } from '@/hooks/useSubmissionLock';
import { type WorkoutAttribution, getProgrammingScoringLabel } from '@ergshoot/shared';
import { AlertCircle, Loader2, Pencil, Plus, Target, Upload } from 'lucide-react';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CsvBulkUploadDialog } from './programming-workbench/CsvBulkUploadDialog';
import {
  type CsvUploadResult,
  downloadCsvTemplate as triggerCsvTemplateDownload,
} from './programming-workbench/model';
import { useCsvImport } from './programming-workbench/useCsvImport';

interface Standard {
  id: string;
  name: string;
  description: string | null;
  movements: string[];
  scoring_type: string;
  scoring_direction: string;
  equipment: string[];
  track: { id: string; name: string | null } | null;
  category?: string | null;
  workout_type?: string | null;
  coach_notes?: string | null;
  duration_minutes?: number | null;
  instructions?: string | null;
  created_at: string;
  attribution?: WorkoutAttribution | null;
  created_by_username?: string | null;
}

interface Track {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  workout_count: number;
  created_at: string;
}

interface CreateStandardPayload {
  name: string;
  description: string;
  scoring_type: string;
  scoring_direction: string;
  movements: string[];
  equipment: string[];
  track_id?: string;
  scheduled_date: string;
  category?: string;
  workout_type: 'standard';
}

interface UpdateStandardPayload {
  name: string;
  description: string;
  scoring_type: string;
  scoring_direction: string;
  movements: string[];
  equipment: string[];
  track_id?: string;
  category?: string;
  workout_type: 'standard';
}

interface StandardsCsvUploadResult extends CsvUploadResult {
  created_workouts: Array<{ row: number; name: string; id: string }>;
}

export default function AdminStandards() {
  const adminApi = useAdminApi();

  // Data state
  const [standards, setStandards] = useState<Standard[]>([]);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingStandard, setEditingStandard] = useState<Standard | null>(null);
  const [formInitialValues, setFormInitialValues] = useState<Partial<WorkoutFormData>>();
  const [isSaving, setIsSaving] = useState(false);
  const createSubmission = useSubmissionLock<CreateStandardPayload>();
  const updateSubmission = useSubmissionLock<UpdateStandardPayload>();
  const isSubmissionLocked = createSubmission.isLocked || updateSubmission.isLocked;

  // Bulk upload state
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const csvImport = useCsvImport<StandardsCsvUploadResult>();

  // Selection state for bulk delete
  const selection = useSelection();
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Filter state (E-4)
  const [filters, setFilters] = useState<FilterValues>(emptyFilterValues);
  const hasActiveFilters = Object.values(filters).some((v) => v !== '');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Standard style options (from Matt's programming logic doc)
  const standardStyles = [
    { value: '', label: 'Select style...' },
    { value: 'short_sprint', label: 'Short Sprint' },
    { value: 'long_sprint', label: 'Long Sprint' },
    { value: 'middle_distance', label: 'Middle Distance' },
    { value: 'enduro_sprint', label: 'Enduro Sprint' },
    { value: 'enduro_long', label: 'Enduro Long' },
    { value: 'enduro_medley', label: 'Enduro Medley' },
  ];

  // Fetch data on mount
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [workoutsRes, tracksRes] = await Promise.all([
        adminApi.getProgramming({
          limit: 200,
          workout_type: 'standard',
          search: filters.search || undefined,
          category: filters.category || undefined,
          scoring_type: filters.scoring_type || undefined,
          equipment: filters.equipment || undefined,
        }),
        adminApi.getTracks(),
      ]);

      if (tracksRes.success && tracksRes.data) {
        setTracks(tracksRes.data.tracks);
      }

      if (workoutsRes.success && workoutsRes.data) {
        setStandards(workoutsRes.data.workouts);
      }
    } catch (err) {
      console.error('Failed to fetch standards:', err);
      setError('Failed to load standards data');
      toast.error('Failed to load standards data');
    } finally {
      setIsLoading(false);
    }
  }, [adminApi, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Scoring type options (unlocked per UAT Feb 2026 — same as Daily WODs)
  const closeForm = () => {
    createSubmission.reset();
    updateSubmission.reset();
    setShowModal(false);
    setEditingStandard(null);
    setFormInitialValues(undefined);
  };

  // Open modal for new standard
  const handleAddStandard = () => {
    createSubmission.reset();
    updateSubmission.reset();
    setEditingStandard(null);
    setFormInitialValues(undefined);
    setShowModal(true);
  };

  // Open modal for editing
  const handleEditStandard = (standard: Standard) => {
    createSubmission.reset();
    updateSubmission.reset();
    setEditingStandard(standard);
    setFormInitialValues({
      name: standard.name,
      description: standard.description || '',
      movements: standard.movements?.join('\n') || '',
      equipment: standard.equipment?.join(', ') || '',
      category: standard.category || '',
      scoring_type: standard.scoring_type || 'time',
      scoring_direction: standard.scoring_direction || 'ascending',
    });
    setShowModal(true);
  };

  // Save standard
  const handleSave = async (formData: WorkoutFormData) => {
    if (!formData.name.trim()) {
      toast.error('Please enter a name');
      return;
    }

    const normalized = normalizeWorkoutFormData(formData);
    const standardsTrack = tracks.find((t) => t.name?.toLowerCase().includes('standard'));

    if (editingStandard) {
      const payload: UpdateStandardPayload = {
        name: normalized.name,
        description: normalized.description,
        scoring_type: normalized.scoring_type,
        scoring_direction: normalized.scoring_direction,
        movements: normalized.movements,
        equipment: normalized.equipment,
        track_id: standardsTrack?.id,
        category: normalized.category,
        workout_type: 'standard',
      };
      const attempt = updateSubmission.begin(payload);
      if (!attempt) return;

      setIsSaving(true);
      let succeeded = false;
      try {
        const response = await adminApi.updateWorkout(
          editingStandard.id,
          attempt.payload,
          attempt.idempotencyKey
        );

        if (response.success) {
          succeeded = true;
          toast.success('Standard updated successfully');
          closeForm();
          fetchData();
        } else {
          toast.error(response.error || 'Failed to update standard');
        }
      } catch (err) {
        console.error('Save error:', err);
        toast.error('Failed to save standard');
      } finally {
        if (succeeded) updateSubmission.reset();
        else updateSubmission.release();
        setIsSaving(false);
      }
      return;
    }

    const payload: CreateStandardPayload = {
      name: normalized.name,
      description: normalized.description,
      scoring_type: normalized.scoring_type,
      scoring_direction: normalized.scoring_direction,
      movements: normalized.movements,
      equipment: normalized.equipment,
      track_id: standardsTrack?.id,
      // BUG-014 fix: send a sentinel date instead of empty string.
      scheduled_date: '9999-12-31',
      category: normalized.category,
      workout_type: 'standard',
    };
    const attempt = createSubmission.begin(payload);
    if (!attempt) return;

    setIsSaving(true);
    let succeeded = false;
    try {
      const response = await adminApi.createWorkout(attempt.payload, attempt.idempotencyKey);

      if (response.success) {
        succeeded = true;
        toast.success('Standard created successfully');
        closeForm();
        fetchData();
      } else {
        toast.error(response.error || 'Failed to create standard');
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save standard');
    } finally {
      if (succeeded) createSubmission.reset();
      else createSubmission.release();
      setIsSaving(false);
    }
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    const ids = Array.from(selection.selectedIds);
    if (ids.length === 0) return;

    if (
      !confirm(`Are you sure you want to delete ${ids.length} standard(s)? This cannot be undone.`)
    ) {
      return;
    }

    setIsBulkDeleting(true);
    try {
      const response = await adminApi.bulkDeleteWorkouts(ids);
      if (response.success && response.data) {
        const { deleted, skipped, details } = response.data;
        if (deleted > 0) {
          toast.success(`Deleted ${deleted} standard(s)`);
        }
        if (skipped > 0) {
          const reasons = details.skipped.map((s) => s.reason).join('; ');
          toast.warning(`${skipped} skipped: ${reasons}`);
        }
        selection.deselectAll();
        fetchData();
      } else {
        toast.error(response.error || 'Bulk delete failed');
      }
    } catch (err) {
      console.error('Bulk delete error:', err);
      toast.error('Failed to delete standards');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const downloadCsvTemplate = () => {
    const template =
      'Categories,title,description,duration,workout,equipment,Coach notes,video_url,metrics\n"Short Sprint","5 x 500m Row on 3min rest","A 500m row benchmark","5","500m Row","RowErg","Pacing guidance: go out at 85%","","time"';
    triggerCsvTemplateDownload(template, 'standards_upload_template.csv');
  };

  const handleBulkUpload = () =>
    csvImport.runUpload({
      upload: (csvContent) => adminApi.bulkUploadStandards(csvContent),
      onSuccess: (result) => {
        if (result.created > 0) {
          toast.success(`${result.created} standard(s) created!`);
          fetchData();
        }
        if (result.errors > 0) {
          toast.error(`${result.errors} row(s) had errors`);
        }
      },
      failureMessage: 'Bulk upload failed',
      unexpectedMessage: 'Failed to process bulk upload',
    });

  // Standards are now filtered server-side via API params
  const filteredStandards = standards;

  // Scoring type label lookup for table display
  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gradient-gold">The Standards</h1>
          <p className="mt-1 text-muted-foreground">
            Manage benchmark workouts for fitness testing and leaderboards.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setShowBulkUploadModal(true);
              csvImport.reset();
            }}
          >
            <Upload className="h-4 w-4 mr-2" />
            Bulk Upload
          </Button>
          <Button className="gap-2" onClick={handleAddStandard}>
            <Plus className="h-4 w-4" />
            Add Standard
          </Button>
        </div>
      </div>

      {/* Filters (E-4) */}
      <FilterBar
        config={{
          showSearch: true,
          showCategory: true,
          showScoring: true,
          showDateRange: false,
          showEquipment: true,
          categoryOptions: standardStyles.filter((s) => s.value !== ''),
        }}
        values={filters}
        onChange={setFilters}
        resultCount={filteredStandards.length}
      />

      {/* Error State */}
      {error && (
        <Card className="mb-6 border-destructive">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
              <Button variant="outline" size="sm" className="ml-auto" onClick={fetchData}>
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Standards Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            Benchmark Workouts
            <Badge variant="secondary" className="ml-2">
              {filteredStandards.length} standards
            </Badge>
          </CardTitle>
          <CardDescription>
            Standardized workouts used for fitness testing and global rankings
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredStandards.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <Target className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-1">No standards found</h3>
              <p className="text-muted-foreground max-w-md">
                {hasActiveFilters
                  ? 'Try adjusting your search criteria.'
                  : 'Create your first benchmark workout to get started.'}
              </p>
              {!hasActiveFilters && (
                <Button className="mt-4" onClick={handleAddStandard}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Standard
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={selection.isAllSelected(filteredStandards.map((s) => s.id))}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            selection.selectAll(filteredStandards.map((s) => s.id));
                          } else {
                            selection.deselectAll();
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead>Workout</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Scoring</TableHead>
                    <TableHead>Movements</TableHead>
                    <TableHead>Equipment</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStandards.map((standard) => {
                    const isExpanded = expandedId === standard.id;
                    return (
                      <Fragment key={standard.id}>
                        <TableRow
                          className="animate-fade-in cursor-pointer hover:bg-accent/50"
                          onClick={() => setExpandedId(isExpanded ? null : standard.id)}
                        >
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selection.isSelected(standard.id)}
                              onCheckedChange={() => selection.toggle(standard.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{standard.name}</p>
                              {!isExpanded && standard.description && (
                                <p className="text-sm text-muted-foreground line-clamp-1">
                                  {standard.description}
                                </p>
                              )}
                              <WorkoutCreatorMetadata
                                attribution={standard.attribution}
                                created_at={standard.created_at}
                                created_by_username={standard.created_by_username}
                                className="mt-1"
                              />
                            </div>
                          </TableCell>
                          <TableCell>
                            {standard.category ? (
                              <Badge variant="secondary" className="text-xs">
                                {standardStyles.find((s) => s.value === standard.category)?.label ||
                                  standard.category}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <Badge variant="outline">
                                {getProgrammingScoringLabel(standard.scoring_type)}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {standard.scoring_direction === 'ascending'
                                  ? 'Lower is better'
                                  : 'Higher is better'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {standard.movements?.length > 0 ? (
                              <span className="text-sm">
                                {standard.movements.slice(0, 2).join(', ')}
                                {standard.movements.length > 2 &&
                                  ` +${standard.movements.length - 2}`}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {standard.equipment?.length > 0 ? (
                              <span className="text-sm">
                                {standard.equipment.slice(0, 2).join(', ')}
                                {standard.equipment.length > 2 &&
                                  ` +${standard.equipment.length - 2}`}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1">
                              <TVDisplayButton workoutId={standard.id} />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditStandard(standard)}
                              >
                                <Pencil className="h-3 w-3 mr-1" />
                                Edit
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow className="cursor-pointer" onClick={() => setExpandedId(null)}>
                            <TableCell colSpan={7} className="p-0">
                              <WorkoutDetail
                                workout={standard}
                                onClose={() => setExpandedId(null)}
                              />
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
        <BulkActionBar
          count={selection.count}
          onDelete={handleBulkDelete}
          onCancel={selection.deselectAll}
          isDeleting={isBulkDeleting}
        />
      </Card>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg mx-4 animate-fade-in max-h-[90vh] overflow-y-auto">
            <WorkoutForm
              variant="standard"
              title={editingStandard ? 'Edit Standard' : 'Add Standard'}
              subtitle={
                editingStandard
                  ? 'Update benchmark workout details'
                  : 'Create a new benchmark workout for testing'
              }
              submitLabel={editingStandard ? 'Save Changes' : 'Create Standard'}
              headerIcon={<Target className="h-5 w-5 text-primary" />}
              initialValues={formInitialValues}
              isSubmitting={isSubmissionLocked || isSaving}
              fieldIds={{
                name: 'standard-name',
                description: 'standard-description',
                movements: 'standard-movements',
                equipment: 'standard-equipment',
              }}
              onSubmit={handleSave}
              onCancel={closeForm}
            />
          </div>
        </div>
      )}
      {showBulkUploadModal && (
        <CsvBulkUploadDialog
          title="Bulk Upload Standards"
          description="Upload multiple standards at once using a CSV file."
          destination="Standards"
          csvContent={csvImport.csvContent}
          fileName={csvImport.fileName}
          result={csvImport.result}
          isUploading={csvImport.isUploading}
          onFileSelect={csvImport.onFileSelect}
          onUpload={handleBulkUpload}
          onClose={() => {
            setShowBulkUploadModal(false);
            csvImport.reset();
          }}
          onDownloadTemplate={downloadCsvTemplate}
          truncatePreviewLines={false}
          instructions={
            <>
              <p className="font-medium text-sm text-foreground mb-1">Column format:</p>
              <p>
                <strong>Categories</strong> (style: Short Sprint, Long Sprint, etc.),{' '}
                <strong>title</strong> (required), <strong>description</strong>
              </p>
              <p>
                <strong>duration</strong>, <strong>workout</strong> (pipe-separated),{' '}
                <strong>equipment</strong> (pipe-separated)
              </p>
              <p>
                <strong>Coach notes</strong>, <strong>video_url</strong>, <strong>metrics</strong>{' '}
                (required: time/reps/rounds_reps/distance/calories)
              </p>
            </>
          }
        />
      )}
    </DashboardLayout>
  );
}
